import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Download, Zap, Award, ArrowRight, ExternalLink } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { Link, useLocation } from 'react-router-dom';

// Mapping of industry slugs to their comparison pages (37 total)
const COMPARISON_PAGES = {
  'plumber': '/best-crm-for-plumbers-uk',
  'electrician': '/best-crm-for-electricians-uk',
  'dental': '/best-crm-for-dentists-uk',
  'hair-salon': '/best-crm-for-hair-salons-uk',
  'roofing': '/best-crm-for-roofers-uk',
  'real-estate-agent': '/best-crm-for-estate-agents-uk',
  'landscaping': '/best-crm-for-landscapers-uk',
  'hvac': '/best-crm-for-hvac-uk',
  'automotive-repair': '/best-crm-for-auto-repair-uk',
  'accounting-firm': '/best-crm-for-accountants-uk',
  'chiropractor': '/best-crm-for-chiropractors-uk',
  'personal-trainer': '/best-crm-for-personal-trainers-uk',
  'cleaning-service': '/best-crm-for-cleaning-services-uk',
  'pest-control': '/best-crm-for-pest-control-uk',
  'veterinary': '/best-crm-for-vets-uk',
  'photography': '/best-crm-for-photographers-uk',
  'it-services': '/best-crm-for-it-services-uk',
  'lawn-care': '/best-crm-for-lawn-care-uk',
  'massage-therapy': '/best-crm-for-massage-therapists-uk',
  'marketing-agency': '/best-crm-for-marketing-agencies-uk',
  'handyman': '/best-crm-for-handyman-uk',
  'painting': '/best-crm-for-painters-uk',
  'tutoring': '/best-crm-for-tutors-uk',
  'pet-grooming': '/best-crm-for-pet-groomers-uk',
  'locksmith': '/best-crm-for-locksmiths-uk',
  'tree-service': '/best-crm-for-tree-service-uk',
  'solar-installation': '/best-crm-for-solar-installers-uk',
  'insurance-agency': '/best-crm-for-insurance-agencies-uk',
  'moving-company': '/best-crm-for-moving-companies-uk',
  'catering': '/best-crm-for-caterers-uk',
  'window-cleaning': '/best-crm-for-window-cleaners-uk',
  'auto-detailing': '/best-crm-for-auto-detailers-uk',
  'towing-service': '/best-crm-for-towing-companies-uk',
  'martial-arts': '/best-crm-for-martial-arts-uk',
  'dance-studio': '/best-crm-for-dance-studios-uk',
  'spa-services': '/best-crm-for-spas-uk',
  'optometry': '/best-crm-for-optometrists-uk'
};

const IndustryPageTemplate = ({ industry }) => {
  const { formatPrice } = useCurrency();
  const location = useLocation();
  
  if (!industry) {
    return <div>Loading...</div>;
  }

  // Extract slug from URL path (e.g., /industries/electrician -> electrician)
  const slug = location.pathname.split('/').pop();
  const comparisonPage = COMPARISON_PAGES[slug];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42]"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block bg-[#FF5A3D]/20 backdrop-blur-sm border border-[#FF5A3D]/30 px-6 py-3 rounded-full mb-6">
              <span className="text-white font-semibold">{industry.icon} FREE Industry Snapshot Included</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {industry.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              {industry.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button 
                asChild
                className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg px-10 py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
              >
                <a href="/pricing">
                  Get Your Free {industry.name} Snapshot
                  <ArrowRight className="ml-2" size={20} />
                </a>
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-6 justify-center text-sm text-gray-300">
              <div className="flex items-center space-x-2">
                <CheckCircle size={20} className="text-green-400" />
                <span>14-Day Trial for {formatPrice(5)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={20} className="text-green-400" />
                <span>Pre-Built {industry.name} Templates</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={20} className="text-green-400" />
                <span>Setup in 5 Minutes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Page Link - Conditional */}
      {comparisonPage && (
        <section className="py-6 px-4 bg-blue-50 border-y border-blue-200">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-lg font-semibold text-[#0E163B] mb-1">
                  🏆 Want to compare CRM options?
                </p>
                <p className="text-gray-600">
                  See how Local Business Heroes stacks up against other CRM systems for {industry.name.toLowerCase()}s
                </p>
              </div>
              <Button 
                asChild
                variant="outline"
                className="border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D] hover:text-white font-semibold whitespace-nowrap"
              >
                <Link to={comparisonPage}>
                  View Comparison Guide <ExternalLink className="ml-2" size={16} />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Problem Section - Red Theme */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              THE PROBLEM
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Running a {industry.name} Business Is Hard
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-12 leading-relaxed">
              Every day you're juggling customers, managing schedules, and trying to grow — 
              but these challenges keep holding you back...
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industry.challenges && industry.challenges.map((challenge, index) => (
              <div key={index} className="bg-white border-2 border-red-200 hover:border-red-400 rounded-xl p-8 transition-all duration-300 shadow-sm hover:shadow-lg group">
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">⚠️</div>
                <p className="text-gray-800 font-semibold text-lg leading-relaxed">{challenge}</p>
              </div>
            ))}
          </div>

          {/* Agitation */}
          <div className="mt-16 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-10 text-white shadow-2xl">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">Here's What This Really Costs You...</h3>
              <div className="grid md:grid-cols-3 gap-8 mt-8">
                <div>
                  <div className="text-4xl font-bold mb-2">💸</div>
                  <div className="text-xl font-semibold mb-2">Lost Revenue</div>
                  <p className="text-red-100">Every missed call is money in your competitor's pocket</p>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">⏰</div>
                  <div className="text-xl font-semibold mb-2">Wasted Time</div>
                  <p className="text-red-100">Hours spent on admin instead of billable work</p>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">😤</div>
                  <div className="text-xl font-semibold mb-2">Stress & Burnout</div>
                  <p className="text-red-100">Constantly putting out fires instead of growing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section - Green Theme */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              THE SOLUTION
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              There's a Better Way to Run Your {industry.name} Business
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Imagine having all your customer communication, scheduling, and follow-ups handled automatically — 
              so you can focus on what you do best
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {industry.solutions && industry.solutions.map((solution, index) => (
              <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                      <CheckCircle className="text-white" size={28} />
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-800 font-semibold text-lg leading-relaxed">{solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Transformation Statement */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-12 text-white text-center shadow-2xl">
            <Zap size={48} className="mx-auto mb-4" />
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Transform Your {industry.name} Business in Just 14 Days
            </h3>
            <p className="text-xl text-green-50 mb-6 max-w-3xl mx-auto">
              Our pre-built snapshot gives you everything you need to start capturing more leads, 
              keeping customers engaged, and growing your business — no tech skills required
            </p>
            <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
              <span className="font-semibold">✨ Setup in 5 minutes • Works 24/7 • Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Download size={48} className="mx-auto text-[#FF5A3D] mb-4" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Your {industry.name} Snapshot Includes
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Everything pre-configured and ready to deploy in minutes
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industry.features && industry.features.map((feature, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 hover:border-[#FF5A3D] rounded-xl p-6 transition-all duration-300 shadow-sm hover:shadow-lg group">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#FF5A3D] flex-shrink-0 group-hover:scale-110 transition-transform" size={24} />
                  <p className="text-gray-700 font-medium leading-relaxed">{feature}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      {industry.reviews && industry.reviews.length > 0 && (
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                REAL CUSTOMER REVIEWS
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                What {industry.name} Businesses Are Saying
              </h2>
              <p className="text-xl text-gray-600">
                Real results from real businesses in your industry
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industry.reviews.map((review, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-[#FF5A3D] hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    {/* Star Rating */}
                    <div className="flex items-center space-x-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                    </div>

                    {/* Review Text */}
                    <p className="text-gray-700 mb-4 leading-relaxed italic">
                      "{review.text}"
                    </p>

                    {/* Result Badge */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center space-x-2">
                        <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm font-semibold text-green-800">{review.result}</span>
                      </div>
                    </div>

                    {/* Author Info */}
                    <div className="border-t pt-4">
                      <div className="font-semibold text-[#0E163B]">{review.author}</div>
                      <div className="text-sm text-gray-500">{review.location}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* Success Stats */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Award size={48} className="mx-auto text-[#FF5A3D] mb-4" />
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Real Results from {industry.name} Businesses
            </h2>
            <p className="text-xl text-gray-300">
              See what you can achieve with our platform
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industry.results && industry.results.map((result, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center hover:bg-white/15 transition-all duration-300">
                <Award size={40} className="mx-auto text-[#FF5A3D] mb-4" />
                <div className="text-lg text-gray-200 font-semibold leading-relaxed">{result}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Start Growing Your {industry.name} Business Today
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join hundreds of successful {industry.name} businesses already using Local Business Heroes
          </p>
          
          <Button 
            asChild
            className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-xl px-10 py-6 rounded-xl transition-all duration-300 transform hover:scale-105"
          >
            <a href="/pricing">
              Get Your Free Snapshot - Start {formatPrice(5)} Trial
            </a>
          </Button>
          
          <p className="text-white/80 mt-6 text-sm">
            Credit card required • Cancel anytime • Setup in 5 minutes
          </p>
        </div>
      </section>
    </div>
  );
};

export default IndustryPageTemplate;
